/**
 * Resupply Transport Modeling - PSP/CRE Tool v2.0
 * Phase 4.6: Route-Based Delivery Time and Success Probability
 * 
 * Models realistic transport constraints for resupply operations:
 * - Route conditions (OPEN, DAMAGED, CONTESTED, CLOSED)
 * - Weather impact on delivery times
 * - Enemy air defense threat to air transport (time AND success probability)
 * - Delivery success probability by route and method
 * 
 * VERSION: 1.1.0 (January 30, 2026)
 * 
 * CHANGELOG:
 * v1.1.0 (January 30, 2026):
 * - ADDED: AD threat now affects delivery TIME for air methods
 *   - Air deliveries must take longer routes to avoid AD threat zones
 *   - Time modifiers: NONE 1.0x, LOW 1.1x, MEDIUM 1.3x, HIGH 1.6x, EXTREME 2.0x
 *   - Ground deliveries NOT affected (AD only affects air)
 * - Added getADThreatTimeModifier() helper function
 * - Updated calculateDeliveryTime() to include ad_threat_modifier in output
 * - Total delivery time now: base × route × weather × AD threat
 * 
 * v1.0.0 (January 29, 2026):
 * - Initial implementation
 * 
 * Author: Dr. Tatar Radu MD
 * Date: January 30, 2026
 */

// ============================================================================
// ROUTE CONDITION MODIFIERS
// ============================================================================

/**
 * Route status definitions with delivery time and success rate modifiers
 */
export const ROUTE_STATUS = {
  OPEN: {
    id: 'OPEN',
    label: 'Open',
    description: 'Route fully operational',
    time_modifier: 1.0,    // No delay
    success_rate: 0.98,    // 98% success
    color: 'success'
  },
  DAMAGED: {
    id: 'DAMAGED',
    label: 'Damaged',
    description: 'Route passable but degraded (craters, debris)',
    time_modifier: 1.5,    // 50% longer
    success_rate: 0.85,    // 85% success
    color: 'warning'
  },
  CONTESTED: {
    id: 'CONTESTED',
    label: 'Contested',
    description: 'Under enemy fire, requires security escort',
    time_modifier: 2.0,    // 2x longer (slower movement)
    success_rate: 0.60,    // 60% success
    color: 'error'
  },
  CLOSED: {
    id: 'CLOSED',
    label: 'Closed',
    description: 'Route impassable, must use alternate',
    time_modifier: 999,    // Effectively infinite
    success_rate: 0.0,     // 0% success
    color: 'error'
  }
};

/**
 * Weather impact on delivery times and success rates
 */
export const WEATHER_IMPACT = {
  CLEAR: {
    id: 'CLEAR',
    time_modifier: 1.0,
    success_modifier: 1.0,
    description: 'No weather delays'
  },
  RAIN: {
    id: 'RAIN',
    time_modifier: 1.2,     // 20% slower
    success_modifier: 0.95, // 5% reduction
    description: 'Light rain, reduced visibility'
  },
  HEAVY_RAIN: {
    id: 'HEAVY_RAIN',
    time_modifier: 1.5,     // 50% slower
    success_modifier: 0.85, // 15% reduction
    description: 'Heavy rain, poor visibility, flooding risk'
  },
  SNOW: {
    id: 'SNOW',
    time_modifier: 1.8,     // 80% slower
    success_modifier: 0.80, // 20% reduction
    description: 'Snow/ice, hazardous driving'
  },
  FOG: {
    id: 'FOG',
    time_modifier: 1.3,     // 30% slower
    success_modifier: 0.90, // 10% reduction (mainly affects air)
    description: 'Dense fog, limited visibility'
  },
  // 2026-10-20 (W1): mud season. EDUCATED GUESS: convoys at about half speed on mud roads (the road tables carry speed 0.3 off paved roads).
  RASPUTITSA: {
    id: 'RASPUTITSA',
    time_modifier: 2.0,     // twice the time
    success_modifier: 0.75, // 25% reduction
    description: 'Mud season: unpaved roads nearly impassable, convoys slow and often stuck'
  }
};

/**
 * Enemy air defense threat levels affecting air delivery
 */
export const AD_THREAT = {
  NONE: {
    id: 'NONE',
    success_modifier: 1.0,
    description: 'No AD threat'
  },
  LOW: {
    id: 'LOW',
    success_modifier: 0.95,
    description: 'Sporadic MANPADS'
  },
  MEDIUM: {
    id: 'MEDIUM',
    success_modifier: 0.80,
    description: 'Active MANPADS + mobile AD'
  },
  HIGH: {
    id: 'HIGH',
    success_modifier: 0.60,
    description: 'Integrated AD network'
  },
  EXTREME: {
    id: 'EXTREME',
    success_modifier: 0.30,
    description: 'Peer AD with long-range SAMs'
  }
};

// ============================================================================
// BASE DELIVERY TIMES (from resupplyThresholds.js)
// ============================================================================

/**
 * Base delivery times by source and method (hours)
 * These are BASELINE times under ideal conditions (OPEN route, CLEAR weather)
 */
export const BASE_DELIVERY_TIMES = {
  ROLE_3: {
    BLUE_LIGHT: 2,   // Helicopter from Corps hospital
    AIR: 4,          // Fixed-wing
    GROUND: 8        // Convoy
  },
  ROLE_4: {
    BLUE_LIGHT: 6,
    AIR: 12,
    GROUND: 24
  },
  THEATER_DEPOT: {
    BLUE_LIGHT: 8,
    AIR: 16,
    GROUND: 48
  },
  MULTINATIONAL_SUPPLY: {
    BLUE_LIGHT: 12,
    AIR: 24,
    GROUND: 72
  },
  HOST_NATION: {
    BLUE_LIGHT: 4,
    AIR: 8,
    GROUND: 16
  }
};

// ============================================================================
// DELIVERY TIME CALCULATION
// ============================================================================

/**
 * Calculate actual delivery time accounting for all factors
 * 
 * @param {string} source - Supply source (ROLE_3, ROLE_4, etc.)
 * @param {string} method - Delivery method (BLUE_LIGHT, AIR, GROUND)
 * @param {Object} routeConditions - Route status from Tab 3
 * @param {Object} environmental - Weather and AD threat
 * @returns {Object} - Delivery time analysis
 */
export function calculateDeliveryTime(source, method, routeConditions, environmental = {}) {
  // Get base delivery time
  const baseTime = BASE_DELIVERY_TIMES[source]?.[method] || 24;
  
  // Extract environmental factors
  const weather = environmental.weather || 'CLEAR';
  const adThreat = environmental.ad_threat || 'NONE';
  
  // Get route modifier (reverse direction: supply comes FROM higher echelon TO lower)
  // Resupply uses reverse route: ROLE_3 → ROLE_2E → ROLE_2B → ROLE_1
  let routeModifier = 1.0;
  let worstRouteStatus = 'OPEN';
  
  if (routeConditions && method === 'GROUND') {
    // Ground transport affected by route conditions
    // Check relevant segments based on source
    const relevantSegments = getRelevantSupplyRoutes(source);
    
    for (const segment of relevantSegments) {
      const segmentRoute = routeConditions[segment];
      if (segmentRoute?.primary?.status) {
        const status = ROUTE_STATUS[segmentRoute.primary.status];
        if (status && status.time_modifier > routeModifier) {
          routeModifier = status.time_modifier;
          worstRouteStatus = segmentRoute.primary.status;
        }
      }
    }
  }
  
  // Weather modifier (affects all methods)
  const weatherModifier = WEATHER_IMPACT[weather]?.time_modifier || 1.0;
  
  // AD threat modifier (affects AIR methods only - forces longer routes to avoid threats)
  const adThreatModifier = getADThreatTimeModifier(method, adThreat);
  
  // Calculate total time: base × route × weather × AD threat
  const totalTimeHours = baseTime * routeModifier * weatherModifier * adThreatModifier;
  
  return {
    base_time_hours: baseTime,
    route_modifier: routeModifier,
    weather_modifier: weatherModifier,
    ad_threat_modifier: adThreatModifier,
    total_time_hours: Math.round(totalTimeHours * 10) / 10,
    worst_route_status: worstRouteStatus,
    weather_condition: weather,
    ad_threat_level: adThreat,
    factors: {
      route: `${worstRouteStatus} (${routeModifier}x)`,
      weather: `${weather} (${weatherModifier}x)`,
      ad_threat: method === 'BLUE_LIGHT' || method === 'AIR' 
        ? `${adThreat} (${adThreatModifier}x)` 
        : 'N/A (ground)'
    }
  };
}

/**
 * Calculate AD threat time modifier for air delivery methods
 * Air methods must take longer routes to avoid AD threat zones
 * 
 * @param {string} method - Delivery method (BLUE_LIGHT, AIR, GROUND)
 * @param {string} adThreat - AD threat level (NONE, LOW, MEDIUM, HIGH, EXTREME)
 * @returns {number} - Time modifier (1.0 = no delay, >1.0 = longer route required)
 */
function getADThreatTimeModifier(method, adThreat) {
  // Ground methods not affected by AD threat for delivery time
  if (method === 'GROUND') {
    return 1.0;
  }
  
  // Air methods (BLUE_LIGHT and AIR) must take longer routes to avoid AD
  switch (adThreat) {
    case 'NONE':
      return 1.0;   // Direct route, no threats
    case 'LOW':
      return 1.1;   // +10% - Slight detour to avoid sporadic MANPADS
    case 'MEDIUM':
      return 1.3;   // +30% - Longer route to avoid mobile AD systems
    case 'HIGH':
      return 1.6;   // +60% - Major detours around integrated AD network
    case 'EXTREME':
      return 2.0;   // +100% - Very long routes to avoid long-range SAMs
    default:
      return 1.0;
  }
}

/**
 * Get relevant supply route segments based on source echelon
 * Supply flows from higher echelon down to lower
 */
function getRelevantSupplyRoutes(source) {
  switch (source) {
    case 'ROLE_3':
      // Corps to Division to Brigade to Battalion
      return ['r2e_to_r3', 'r2b_to_r2e', 'r1_to_r2b'];
    case 'ROLE_4':
      // Theater to Corps to Division to Brigade to Battalion
      return ['r3_to_r4', 'r2e_to_r3', 'r2b_to_r2e', 'r1_to_r2b'];
    case 'THEATER_DEPOT':
    case 'MULTINATIONAL_SUPPLY':
      // Strategic level - all routes
      return ['r3_to_r4', 'r2e_to_r3', 'r2b_to_r2e', 'r1_to_r2b'];
    case 'HOST_NATION':
      // Local support - only tactical routes
      return ['r1_to_r2b'];
    default:
      return ['r1_to_r2b'];
  }
}

// ============================================================================
// DELIVERY SUCCESS PROBABILITY
// ============================================================================

/**
 * Calculate probability of successful delivery
 * 
 * @param {string} method - Delivery method (BLUE_LIGHT, AIR, GROUND)
 * @param {Object} routeConditions - Route status from Tab 3
 * @param {Object} environmental - Weather and AD threat
 * @param {string} source - Supply source
 * @returns {Object} - Success probability analysis
 */
export function calculateDeliveryProbability(method, routeConditions, environmental = {}, source = 'ROLE_3') {
  // Extract environmental factors
  const weather = environmental.weather || 'CLEAR';
  const adThreat = environmental.ad_threat || 'NONE';
  
  // Get worst route condition
  let worstRouteSuccess = 0.98;
  let worstRouteStatus = 'OPEN';
  
  if (routeConditions) {
    const relevantSegments = getRelevantSupplyRoutes(source);
    
    for (const segment of relevantSegments) {
      const segmentRoute = routeConditions[segment];
      if (segmentRoute?.primary?.status) {
        const status = ROUTE_STATUS[segmentRoute.primary.status];
        if (status && status.success_rate < worstRouteSuccess) {
          worstRouteSuccess = status.success_rate;
          worstRouteStatus = segmentRoute.primary.status;
        }
      }
    }
  }
  
  // Weather modifier
  const weatherModifier = WEATHER_IMPACT[weather]?.success_modifier || 1.0;
  
  // AD threat modifier (only affects air)
  let adModifier = 1.0;
  if (method === 'BLUE_LIGHT' || method === 'AIR') {
    adModifier = AD_THREAT[adThreat]?.success_modifier || 1.0;
  }
  
  // Calculate compound probability
  const totalProbability = worstRouteSuccess * weatherModifier * adModifier;
  
  return {
    probability: Math.round(totalProbability * 1000) / 1000,
    percentage: Math.round(totalProbability * 100),
    route_factor: worstRouteSuccess,
    weather_factor: weatherModifier,
    ad_threat_factor: adModifier,
    worst_route_status: worstRouteStatus,
    risk_level: getRiskLevel(totalProbability),
    factors: {
      route: `${worstRouteStatus} (${Math.round(worstRouteSuccess * 100)}%)`,
      weather: `${weather} (${Math.round(weatherModifier * 100)}%)`,
      ad_threat: method === 'BLUE_LIGHT' || method === 'AIR' 
        ? `${adThreat} (${Math.round(adModifier * 100)}%)` 
        : 'N/A (ground)'
    }
  };
}

/**
 * Determine risk level based on success probability
 */
function getRiskLevel(probability) {
  if (probability >= 0.95) return 'LOW';
  if (probability >= 0.80) return 'MEDIUM';
  if (probability >= 0.60) return 'HIGH';
  return 'CRITICAL';
}

// ============================================================================
// MONTE CARLO SIMULATION
// ============================================================================

/**
 * Run Monte Carlo simulation of resupply schedule
 * 
 * @param {Array} resupplyEvents - Scheduled resupply events
 * @param {Object} initialStock - Starting blood stock
 * @param {Object} dailyConsumption - Daily consumption rates
 * @param {number} duration - Operation duration (days)
 * @param {Object} routeConditions - Route status
 * @param {Object} environmental - Weather and AD threat
 * @param {number} iterations - Number of simulation runs (default 100)
 * @returns {Object} - Monte Carlo results with probability bands
 */
export function runMonteCarloResupplySimulation(
  resupplyEvents,
  initialStock,
  dailyConsumption,
  duration,
  routeConditions,
  environmental,
  iterations = 100
) {
  const results = [];
  
  // Run iterations
  for (let i = 0; i < iterations; i++) {
    const outcome = simulateSingleRun(
      resupplyEvents,
      initialStock,
      dailyConsumption,
      duration,
      routeConditions,
      environmental
    );
    results.push(outcome);
  }
  
  // Calculate statistics
  const finalDOSValues = results.map(r => r.final_dos);
  const minDOSValues = results.map(r => r.minimum_dos);
  const stockoutDays = results.map(r => r.stockout_day).filter(d => d !== null);
  
  finalDOSValues.sort((a, b) => a - b);
  minDOSValues.sort((a, b) => a - b);
  
  const p5Index = Math.floor(iterations * 0.05);
  const p50Index = Math.floor(iterations * 0.50);
  const p95Index = Math.floor(iterations * 0.95);
  
  return {
    iterations,
    final_dos: {
      p5: finalDOSValues[p5Index],    // Worst case (5th percentile)
      p50: finalDOSValues[p50Index],  // Expected (median)
      p95: finalDOSValues[p95Index]   // Best case (95th percentile)
    },
    minimum_dos: {
      p5: minDOSValues[p5Index],
      p50: minDOSValues[p50Index],
      p95: minDOSValues[p95Index]
    },
    stockout_probability: stockoutDays.length / iterations,
    average_stockout_day: stockoutDays.length > 0 
      ? stockoutDays.reduce((a, b) => a + b, 0) / stockoutDays.length 
      : null,
    success_rate: (iterations - stockoutDays.length) / iterations
  };
}

/**
 * Simulate single resupply run
 */
function simulateSingleRun(
  resupplyEvents,
  initialStock,
  dailyConsumption,
  duration,
  routeConditions,
  environmental
) {
  let stock = { ...initialStock };
  let minimumDOS = 999;
  let stockoutDay = null;
  
  for (let day = 1; day <= duration; day++) {
    // Apply resupply events for this day
    const dayEvents = resupplyEvents.filter(e => e.day === day);
    
    for (const event of dayEvents) {
      // Calculate delivery success probability
      const probability = calculateDeliveryProbability(
        event.delivery_method,
        routeConditions,
        environmental,
        event.source
      );
      
      // Random success check
      const deliverySucceeded = Math.random() < probability.probability;
      
      if (deliverySucceeded) {
        // Apply resupply
        stock.prbc += event.units?.prbc || 0;
        stock.ffp += event.units?.ffp || 0;
        stock.plt += event.units?.plt || 0;
        stock.cryo += event.units?.cryo || 0;
      }
      // If failed, no units received
    }
    
    // Consume daily
    stock.prbc -= dailyConsumption.prbc;
    stock.ffp -= dailyConsumption.ffp;
    stock.plt -= dailyConsumption.plt;
    stock.cryo -= dailyConsumption.cryo;
    
    // Calculate DOS
    const dos = Math.min(
      stock.prbc / dailyConsumption.prbc,
      stock.ffp / dailyConsumption.ffp,
      stock.plt / dailyConsumption.plt,
      stock.cryo / dailyConsumption.cryo
    );
    
    if (dos < minimumDOS) minimumDOS = dos;
    if (dos <= 0 && stockoutDay === null) stockoutDay = day;
  }
  
  // Final DOS
  const finalDOS = Math.min(
    stock.prbc / dailyConsumption.prbc,
    stock.ffp / dailyConsumption.ffp,
    stock.plt / dailyConsumption.plt,
    stock.cryo / dailyConsumption.cryo
  );
  
  return {
    final_dos: finalDOS,
    minimum_dos: minimumDOS,
    stockout_day: stockoutDay
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

const resupplyTransportAPI = {
  ROUTE_STATUS,
  WEATHER_IMPACT,
  AD_THREAT,
  BASE_DELIVERY_TIMES,
  calculateDeliveryTime,
  calculateDeliveryProbability,
  runMonteCarloResupplySimulation
};

export default resupplyTransportAPI;
